import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiUrl } from '../utils/apiBase';
import { pluralPL } from '../utils/pluralPL';
import { cleanTournamentName } from '../utils/cleanTournamentName';

interface ClubDetailData {
  club: { id: number; slug: string; name: string };
  tournaments: Array<{
    kind: 'live' | 'archive';
    id: number;
    name: string;
    startDate: string | null;
    endDate: string | null;
    venue: string | null;
    status: string;
    matchCount?: number;
    uniquePlayerCount?: number;
  }>;
  leagues: Array<{
    id: number;
    name: string;
    discipline: string;
    startDate: string;
    endDate: string;
    status: string;
    defaultRating: number | null;
    roundCount: number;
    seasonCount: number;
  }>;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return '—';
  }

  return new Date(dateStr).toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function disciplineLabel(d: string): string {
  if (d === 'SINGLES' || d === 'SINGLES_M' || d === 'SINGLES_F') {
    return 'Singiel';
  }
  if (d === 'DOUBLES' || d === 'DOUBLES_M' || d === 'DOUBLES_F') {
    return 'Debel';
  }
  return d;
}

const ClubDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<ClubDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      return;
    }

    fetch(apiUrl(`/api/public/clubs/${slug}`))
      .then((res) => {
        if (res.status === 404) {
          throw new Error('not_found');
        }
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json() as Promise<ClubDetailData>;
      })
      .then((d) => {
        setData(d);
        setLoading(false);
      })
      .catch((e: Error) => {
        setError(e.message === 'not_found' ? 'Klub nie istnieje.' : 'Nie udało się załadować klubu.');
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return <div className="events-loading">Ładowanie...</div>;
  }

  if (error || !data) {
    return <div className="events-error">{error}</div>;
  }

  return (
    <div className="events-list">
      <Link to="/clubs" className="event-detail-back">
        ← Powrót do klubów
      </Link>

      <div className="events-list-header">
        <div>
          <span className="eyebrow">Klub</span>
          <h1>{data.club.name}</h1>
        </div>
      </div>

      <h2 className="events-list-section-h2">Ligi ({data.leagues.length})</h2>
      {data.leagues.length === 0 ? (
        <p className="events-empty">Brak lig.</p>
      ) : (
        <ul className="events-cards">
          {data.leagues.map((league) => (
            <li key={league.id} className="event-card">
              <h2 className="event-card-name">{cleanTournamentName(league.name)}</h2>
              <div className="event-card-meta">
                <span className="event-card-venue">{disciplineLabel(league.discipline)}</span>
                <span className="event-card-dates">
                  {formatDate(league.startDate)} – {formatDate(league.endDate)}
                </span>
              </div>
              <div className="event-card-meta">
                <span className="event-card-venue">
                  {league.seasonCount} {pluralPL(league.seasonCount, 'sezon', 'sezony', 'sezonów')}
                </span>
                <span className="event-card-dates">
                  {league.roundCount} {pluralPL(league.roundCount, 'kolejka', 'kolejki', 'kolejek')}
                </span>
              </div>
              <Link to={`/leagues/${league.id}`} className="event-card-link">
                Szczegóły
              </Link>
            </li>
          ))}
        </ul>
      )}

      <h2 className="events-list-section-h2">Turnieje ({data.tournaments.length})</h2>
      {data.tournaments.length === 0 ? (
        <p className="events-empty">Brak turniejów.</p>
      ) : (
        <ul className="events-cards">
          {data.tournaments.map((tournament) => {
            const detailPath =
              tournament.kind === 'archive'
                ? `/tournament/${tournament.id}`
                : `/events/${tournament.id}`;
            const cardKey = `${tournament.kind}-${tournament.id}`;

            return (
              <li key={cardKey} className="event-card">
                <h2 className="event-card-name">{cleanTournamentName(tournament.name)}</h2>
                <div className="event-card-meta">
                  <span className="event-card-dates">
                    {formatDate(tournament.startDate)}
                    {tournament.endDate && tournament.endDate !== tournament.startDate
                      ? ` – ${formatDate(tournament.endDate)}`
                      : ''}
                  </span>
                  {tournament.venue ? (
                    <span className="event-card-venue">{tournament.venue}</span>
                  ) : tournament.matchCount != null ? (
                    <span className="event-card-venue">
                      {tournament.matchCount}{' '}
                      {pluralPL(tournament.matchCount, 'mecz', 'mecze', 'meczów')}
                    </span>
                  ) : null}
                </div>
                <Link to={detailPath} className="event-card-link">
                  Szczegóły
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ClubDetail;
